import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { CollapsedPopover } from "@/components/doc/right-sidebar/CollapsedPopover";
import { TableCellBackgroundDropdown } from "@/components/doc/right-sidebar/table-style/TableCellBackgroundDropdown";
import { TableColumnWidth } from "@/components/doc/right-sidebar/table-style/TableColumnWidth";
import { TableRowHeight } from "@/components/doc/right-sidebar/table-style/TableRowHeight";
import { InlineTextStyle } from "@/components/doc/right-sidebar/text-style/InlineTextStyle";
import { Icon } from "@/components/icons/Icon";
import { ActionsDisclosure } from "@/components/sidebar/ActionsDisclosure";
import { SidebarToolbar } from "@/components/sidebar/SidebarToolbar";
import { useTableCellEditorState } from "@/components/tiptap/selectors/useTableEditorState";
import { isCellSelection } from "@/components/tiptap/utils/isCellSelection";
import { preserveMathCursorPosition } from "@/components/tiptap/utils/preserveMathCursorPosition";

import { AlignmentButtons } from "../alignment-buttons/AlignmentButtons";

import { TableCellAlignmentButtons } from "./TableCellAlignmentButtons";

import type { CellVerticalAlignment } from "@/components/tiptap/tables/TableCellVerticalAlignment";
import type { DocumentModel } from "@/models/DocumentModel";
import type { Color } from "@corca/data";

type Props = {
  doc: DocumentModel;
  isCollapsed?: boolean;
};

const Content = observer(({ doc }: { doc: DocumentModel }) => {
  const { tiptap } = doc.editor;
  const t = useTranslations("sidebar");
  const {
    tableCellNode,
    tableHeaderNode,
    backgroundColor,
    verticalAlignment,
    colWidth,
    rowHeight,
  } = useTableCellEditorState(doc) ?? {};
  const tableCell = tableCellNode || tableHeaderNode;

  if (!tiptap || !tableCell) {
    return null;
  }

  const { selection } = tiptap.state;
  const { isExpression } = doc.editor.editorState;
  const canSetColumnWidth = isCellSelection(selection) ? selection.ranges.length === 1 : true;

  const handleRowHeightChange = (value: number | null) => {
    tiptap?.commands.setRowHeight(value);
  };

  const handleBackgroundColorSelect = (color: Color) => {
    tiptap.chain().focus().setCellBackgroundColor(color).run();
  };

  const handleColWidthChange = (value: number | null) => {
    tiptap.commands.setColumnWidth(value);
  };

  const handleCellAlignmentChange = (alignment: CellVerticalAlignment) => {
    preserveMathCursorPosition(doc, () => {
      tiptap.chain().setCellVerticalAlignment(alignment).run();
    });
  };

  return (
    <>
      {!isExpression && <InlineTextStyle doc={doc} />}
      <div className="flex items-center gap-1">
        {canSetColumnWidth && (
          <>
            <TableColumnWidth
              doc={doc}
              tableCell={tableCell}
              value={colWidth}
              onChange={handleColWidthChange}
            />
            <TableRowHeight
              doc={doc}
              tableCell={tableCell}
              value={rowHeight}
              onChange={handleRowHeightChange}
            />
          </>
        )}
        <TableCellBackgroundDropdown
          tooltipTitle={t("tableCellStyle.backgroundColor")}
          currentColor={backgroundColor || "white"}
          onSelect={handleBackgroundColorSelect}
        />
      </div>
      <div className="text-left text-xxxs uppercase text-gray-500 tracking-tight w-full px-1 pt-3 mb-1">
        {t("tableStyle.alignment.title")}
      </div>
      {/* Two radio groups of three: the same columns as a single six-column grid */}
      <SidebarToolbar label={t("toolbars.cellAlignment")} className="grid grid-cols-2 gap-1">
        <AlignmentButtons doc={doc} />
        <TableCellAlignmentButtons
          alignment={verticalAlignment}
          onSelect={handleCellAlignmentChange}
        />
      </SidebarToolbar>
    </>
  );
});

export const TableCellStyle = observer(function TableCellStyle({ doc, isCollapsed }: Props) {
  const t = useTranslations("sidebar");
  const { tiptap } = doc.editor;
  const { address, tableCellNode, tableHeaderNode } = useTableCellEditorState(doc) ?? {};

  const tableCell = tableCellNode || tableHeaderNode;

  if (!tiptap || !tableCell) {
    return null;
  }

  if (isCollapsed) {
    return (
      <CollapsedPopover
        icon={<Icon name="TableCell" />}
        tooltipTitle={t("tableCellStyle.title", { address })}
        title={t("tableCellStyle.title", { address })}
      >
        <Content doc={doc} />
      </CollapsedPopover>
    );
  }

  return (
    <ActionsDisclosure
      data-panel="table_cell_style"
      title={t("tableCellStyle.title", { address })}
      disclosureProps={{ defaultOpen: true }}
      disclosurePanelProps={{ className: "-mt-1.25" }}
    >
      <Content doc={doc} />
    </ActionsDisclosure>
  );
});
